import Cookie from 'js-cookie'

import { POPS_API_VERSION, POPS_REQUEST_SOURCE } from '@/constants/pops'
import { PACK_OF_WEEKS_REDEMPTION_WEEK_CAP } from '@/constants/program-dates'
import {
  ENCODED_CUSTOMER_ID,
  USER_ACCESS_POPS_TOKEN,
  USER_ACCESS_TOKEN,
} from '@/constants/shopify'
import { tryLogout } from '@/stores/account/operations'
import type {
  Addon,
  Address,
  Campaign,
  CustomerSubscriptionCampaignsItem,
  DeliveryZone,
  DiscountOption,
  DiscountOptionWeek,
  Meal,
  MealSettings,
  MealSwap,
  PopsPackage,
  Subscription,
  SubscriptionCampaign,
  SubscriptionWeekMealsResponse,
  Supplement,
  UpdateSubscriptionAddonsPayload,
} from '@/types/pops'
import { getLoginHref } from '@/utilities/auth'
import type { Dayjs } from '@/utilities/dayjs-config'
import { captureException } from '@/utilities/logger-fe'

type ClientParams = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  body?: Record<string, unknown>
  headers?: HeadersInit
  skipLogoutWhenNotAuthorized?: boolean
}

type Token = {
  token: string
  expires_at: number
}

type Tokens = {
  auth_token: Token
  refresh_token: Token
}

const getAuthHeaders = () =>
  ({
    customer_id: Cookie.get(ENCODED_CUSTOMER_ID),
    pops_token: Cookie.get(USER_ACCESS_POPS_TOKEN),
  }) as HeadersInit

export const client = async <T>(
  path: string,
  { method, body, headers, skipLogoutWhenNotAuthorized }: ClientParams = {},
): Promise<{ status: number; body: T } | never> => {
  try {
    const response = await fetch(`/api/pops${path}`, {
      method: method || 'GET',
      ...(body && { body: JSON.stringify(body) }),
      headers: {
        'Content-Type': 'application/json',
        accept: 'application/json',
        'API-VERSION': POPS_API_VERSION,
        'Request-Source': POPS_REQUEST_SOURCE,
        ...headers,
      },
    })

    const data = await response.json().catch(() => null)

    const result = {
      status: response.status,
      body: data,
    }
    if (!response.ok) {
      const isTokenExpiredOrInvalid = response?.status === 401
      if (isTokenExpiredOrInvalid && !skipLogoutWhenNotAuthorized) {
        await tryLogout()
        window.location.href = getLoginHref(window.location.href)
        throw Error(
          'POPS API Error: 401 - Unauthorized. Redirecting to login page.',
        )
      }

      captureException(`POPS API Error: ${JSON.stringify(result)}`)
      return Promise.reject(result)
    }

    return result
  } catch (error) {
    if (typeof error === 'string') {
      throw new Error(error)
    }
    return Promise.reject(error)
  }
}

export const authentication = async (): Promise<Tokens> => {
  const res = await client<Tokens>('/authentication', {
    method: 'POST',
    body: {
      customer_id: Cookie.get(ENCODED_CUSTOMER_ID),
      shopify_token: Cookie.get(USER_ACCESS_TOKEN),
    },
  })

  return res.body
}

export const fetchAddons = async (): Promise<Addon[]> => {
  const res = await client<Addon[]>('/addons')

  return res.body
}

export const fetchSupplements = async (): Promise<Supplement[]> => {
  const res = await client<Supplement[]>('/supplements')

  return res.body
}

export const fetchSubscriptionStartDates = async ({
  zipCode,
  dayCount,
  mealsPerDay,
  weekCount,
}: {
  zipCode: string
  dayCount: number
  mealsPerDay: string
  weekCount: number
}) => {
  const params = new URLSearchParams({
    zip_code: zipCode,
    day_count: dayCount.toString(),
    meals_per_day: mealsPerDay,
    week_count: weekCount.toString(),
  })
  const res = await client<string[]>(
    `/subscription_start_dates?${params.toString()}`,
  )

  return res.body
}

export const fetchDeliveryZoneById = async (
  deliveryZoneId: number,
): Promise<DeliveryZone> => {
  if (!deliveryZoneId)
    throw new Error('/delivery_zones/:id error: id is required')

  const res = await client<DeliveryZone>(`/delivery_zones/${deliveryZoneId}`, {
    headers: getAuthHeaders(),
  })

  return res.body
}

export const fetchDeliveryZoneByZip = async (
  zip: string,
): Promise<DeliveryZone> => {
  if (!zip) throw new Error('/delivery_zones/get_by_zip error: zip is required')

  const params = new URLSearchParams({
    zip_code: zip.toString(),
  })

  const res = await client<DeliveryZone>(
    `/delivery_zones/get_by_zip?${params.toString()}`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const fetchFutureCampaigns = async (
  startDate: string,
): Promise<Campaign[]> => {
  if (!startDate)
    throw new Error('/campaigns/future error: startDate is required')

  const params = new URLSearchParams({
    start_date: startDate,
  })

  const res = await client<Campaign[]>(
    `/campaigns/future_campaigns?${params.toString()}`,
  )

  return res.body
}

export const fetchDiscountOption = async (
  discountOptionId: number,
): Promise<DiscountOption> => {
  if (!discountOptionId)
    throw new Error('/discount_options/:id error: id is required')

  const res = await client<DiscountOption>(
    `/discount_options/${discountOptionId}`,
  )

  return res.body
}

export const fetchSubscriptionDiscountOptions = async (
  subscriptionId: number,
): Promise<DiscountOptionWeek[]> => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/discount_options error: subscriptionId is required',
    )

  const res = await client<DiscountOptionWeek[]>(
    `/subscriptions/${subscriptionId}/discount_options`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const fetchMealSettings = async () => {
  const res = await client<MealSettings>('/recipe_settings')

  return res.body
}

export const getMeals = async (
  week: string,
  kitchenId: string,
  zipcode: string,
) => {
  const params = new URLSearchParams({
    week,
    kitchen_id: kitchenId,
    zipcode,
  })

  const res = await client<Meal[]>(`/meals?${params.toString()}`)

  return res.body
}

export const getMenu = async (week: Dayjs, kitchenId: string, zip: string) => {
  const weekDateString = week.startOf('week').format('YYYY-MM-DD')
  const menu = await getMeals(weekDateString, kitchenId, zip)

  // Filter out meals that have no title
  return menu.filter((meal) => !!meal.title)
}

export const getSubscriptionWeekMeals = async (
  subscriptionId: number,
  week: string,
) => {
  const params = new URLSearchParams({
    week,
  })

  const res = await client<SubscriptionWeekMealsResponse>(
    `/subscriptions/${subscriptionId}/meals?${params.toString()}`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const getWeekMenu = async (subscriptionId: number, week: Dayjs) => {
  const weekString = week.startOf('week').format('YYYY-MM-DD')
  const subscriptionWeekMeals = await getSubscriptionWeekMeals(
    subscriptionId,
    weekString,
  )

  return subscriptionWeekMeals
}

export const getSubscription = async (subscriptionId: number) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id error: id is required')

  const res = await client<Subscription>(`/subscriptions/${subscriptionId}`, {
    headers: getAuthHeaders(),
  })

  return res.body
}

export const cancelSubscription = async (subscriptionId: number) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id/cancel error: id is required')

  const res = await client(`/subscriptions/${subscriptionId}/cancel`, {
    method: 'POST',
    headers: getAuthHeaders(),
  })

  return res.body
}

export const updateSubscription = async (
  subscriptionId: number,
  subscription: {
    sku?: string
    name?: string
    delivery_window?: string
    supplement?: string
    detox_tea?: boolean
    start_day?: string
    delivery_notes?: string
  },
) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id error: id is required')

  const res = await client(`/subscriptions/${subscriptionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: { subscription },
  })

  return res.body
}

export const optInToDiscountOption = async (
  subscriptionId: number,
  discountOptionId: number,
  startWeek: string,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/discount_options error: subscriptionId is required',
    )
  if (!discountOptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/discount_options error: discountOptionId is required',
    )
  if (!startWeek)
    throw new Error(
      '/subscriptions/:subscriptionId/discount_options error: startWeek is required',
    )

  const res = await client(
    `/subscriptions/${subscriptionId}/discount_options`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: {
        discount_option_id: discountOptionId,
        start_week: startWeek,
      },
    },
  )

  return res.body
}

export const getCustomerSubscriptions = async (
  customerId: number,
  {
    page,
    perPage,
    sortBy,
    filterBy,
    skipLogoutWhenNotAuthorized,
  }: {
    page?: number
    perPage?: number
    sortBy?: string
    filterBy?: string
    skipLogoutWhenNotAuthorized?: boolean
  } = {},
) => {
  if (!customerId)
    throw new Error(
      '/customers/:customerId/subscriptions error: customerId is required',
    )

  const params = new URLSearchParams({
    ...(page && { page: page?.toString() }),
    ...(perPage && { per_page: perPage?.toString() }),
    ...(sortBy && { sort_by: sortBy }),
    ...(filterBy && { filter_by: filterBy }),
  })
  const res = await client<Subscription[]>(
    `/customers/${customerId}/subscriptions?${params.toString()}`,
    {
      headers: getAuthHeaders(),
      skipLogoutWhenNotAuthorized,
    },
  )

  // TODO: Move filter logic on the backend
  // Since the site logic is heavily based on delivery zones, filter out
  // subscriptions that are not associated to one
  const subscriptions = res.body
  return subscriptions.filter(
    (subscription) => subscription.delivery_zone_id !== null,
  )
}

export const fetchCustomerSubscriptionCampaigns = async (
  customerId: number,
) => {
  if (!customerId)
    throw new Error(
      '/customers/:customerId/subscription_campaigns error: customerId is required',
    )

  const res = await client<CustomerSubscriptionCampaignsItem[]>(
    `/customers/${customerId}/subscription_campaigns?customer_id=${customerId}`,
    { headers: getAuthHeaders() },
  )

  return res.body || []
}

export const fetchSubscriptionWeek = async (
  subscriptionId: number,
  week: string,
) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id error: id is required')
  if (!week) throw new Error('/subscriptions/:id error: week is required')

  const res = await client<Subscription>(
    `/subscriptions/${subscriptionId}?week=${week}`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const fetchSubscriptionAddons = async (
  subscriptionId: number,
): Promise<Addon[]> => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: subscriptionId is required',
    )

  const res = await client<Addon[]>(
    `/subscriptions/${subscriptionId}/addons?subscription_id=${subscriptionId}`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const fetchSubscriptionWeekAddons = async (
  subscriptionId: number,
  week: string,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: week is required',
    )

  const res = await client(
    `/subscriptions/${subscriptionId}/addons?week=${week}`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const updateSubscriptionAddons = async (
  subscriptionId: number,
  payload: UpdateSubscriptionAddonsPayload,
) => {
  if (!subscriptionId) {
    throw new Error('/subscriptions/:id error: id is required')
  }

  const res = await client(`/subscriptions/${subscriptionId}/addons`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: {
      addons_attributes: payload,
    },
  })

  return res.body
}

export const updateSubscriptionWeekAddons = async (
  subscriptionId: number,
  week: string,
  payload: UpdateSubscriptionAddonsPayload,
  withoutAddons?: boolean,
) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id error: id is required')

  const res = await client(`/subscriptions/${subscriptionId}/addons`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: {
      week,
      addons_attributes: payload,
      ...(withoutAddons && { without_addons: true }),
    },
  })

  return res.body
}

export const setSubscriptionAddon = async (
  subscriptionId: number,
  addonId: number,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: subscriptionId is required',
    )
  if (!addonId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: addonId is required',
    )

  const res = await client(`/subscriptions/${subscriptionId}/addons`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: {
      addon_product_id: addonId,
    },
  })

  return res.body
}

export const setSubscriptionWeekAddon = async (
  subscriptionId: number,
  week: string,
  addonId: number,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: week is required',
    )
  if (!addonId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: addonId is required',
    )

  const res = await client(`/subscriptions/${subscriptionId}/addons`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: {
      week,
      addon_product_id: addonId,
    },
  })

  return res.body
}

export const removeSubscriptionAddon = async (
  subscriptionId: number,
  addonId: number,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: subscriptionId is required',
    )
  if (!addonId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: addonId is required',
    )

  const params = new URLSearchParams({ addon_product_id: addonId.toString() })

  const res = await client(
    `/subscriptions/${subscriptionId}/addons?${params.toString()}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    },
  )

  return res.body
}

export const removeSubscriptionWeekAddon = async (
  subscriptionId: number,
  week: string,
  addonId: number,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: week is required',
    )
  if (!addonId)
    throw new Error(
      '/subscriptions/:subscriptionId/addons error: addonId is required',
    )

  const params = new URLSearchParams({
    week,
    addon_product_id: addonId.toString(),
  })

  const res = await client(
    `/subscriptions/${subscriptionId}/addons?${params.toString()}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    },
  )

  return res.body
}

export const getSubscriptionWeekMealSwaps = async (
  subscriptionId: number,
  week: string,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/meal_swaps error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/meal_swaps error: week is required',
    )

  const res = await client<{ meal_swaps: MealSwap[] }>(
    `/subscriptions/${subscriptionId}/meal_swaps?week=${week}`,
    { headers: getAuthHeaders() },
  )

  return res.body.meal_swaps
}

export const createSubscriptionWeekMealSwap = async (
  subscriptionId: number,
  week: string,
  mealSwap: {
    swap_from_day: number
    swap_to_day?: number
    swap_from_meal: string
    swap_to_meal?: string
    classic_meal?: string
  },
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/meal_swaps error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/meal_swaps error: week is required',
    )
  if (!mealSwap)
    throw new Error(
      '/subscriptions/:subscriptionId/meal_swaps error: mealSwap is required',
    )

  const res = await client(`/subscriptions/${subscriptionId}/meal_swaps`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: {
      week,
      meal_swap: mealSwap,
    },
  })

  return res.body
}

export const removeSubscriptionMealSwap = async (
  subscriptionId: number,
  mealSwapId: number,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/meal_swaps/:mealSwapId error: subscriptionId is required',
    )
  if (!mealSwapId)
    throw new Error(
      '/subscriptions/:subscriptionId/meal_swaps/:mealSwapId error: mealSwapId is required',
    )

  const res = await client(
    `/subscriptions/${subscriptionId}/meal_swaps/${mealSwapId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    },
  )

  return res.body
}

export const updateSubscriptionWeek = async (
  subscriptionId: number,
  week: string,
  subscriptionWeek: {
    sku?: string
    name?: string
    delivery_window?: string
    supplement?: string
    detox_tea?: boolean
    start_day?: string
    delivery_notes?: string
  },
) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id error: id is required')
  if (!week) throw new Error('/subscriptions/:id error: week is required')

  const res = await client(`/subscriptions/${subscriptionId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: {
      week,
      subscription: subscriptionWeek,
    },
  })

  return res.body
}

export const skipSubscriptionWeek = async (
  subscriptionId: number,
  week: string,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/skips error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/skips error: week is required',
    )

  const res = await client(`/subscriptions/${subscriptionId}/skips`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: {
      week,
    },
  })

  return res.body
}

export const activateSubscriptionWeek = async (
  subscriptionId: number,
  week: string,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/skips error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/skips error: week is required',
    )

  const params = new URLSearchParams({
    week,
  })

  const res = await client(
    `/subscriptions/${subscriptionId}/skips?${params.toString()}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    },
  )

  return res.body
}

export const fetchSubscriptionSkippedWeeks = async (
  subscriptionId: number,
): Promise<string[]> => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/skips error: subscriptionId is required',
    )

  const res = await client<{ skipped_weeks: string[] }>(
    `/subscriptions/${subscriptionId}/skips?subscription_id=${subscriptionId}`,
    { headers: getAuthHeaders() },
  )

  return res.body.skipped_weeks
}

export const updateSubscriptionShippingAddress = async (
  subscriptionId: number,
  shippingAddress: Address,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/shipping_address error: subscriptionId is required',
    )

  const res = await client(
    `/subscriptions/${subscriptionId}/shipping_address`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: {
        shipping_address: shippingAddress,
      },
    },
  )

  // TODO: handle errors properly

  return res.body
}

export const updateSubscriptionBillingAddress = async (
  subscriptionId: number,
) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id/billing_address error: id is required')

  const res = await client(`/subscriptions/${subscriptionId}/billing_address`, {
    method: 'PUT',
    headers: getAuthHeaders(),
  })

  return res.body
}

export const updateSubscriptionWeekShippingAddress = async (
  subscriptionId: number,
  week: string,
  shippingAddress: Address,
) => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/shipping_address error: subscriptionId is required',
    )
  if (!week)
    throw new Error(
      '/subscriptions/:subscriptionId/shipping_address error: week is required',
    )

  const res = await client(
    `/subscriptions/${subscriptionId}/shipping_address`,
    {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: {
        week,
        shipping_address: shippingAddress,
      },
    },
  )

  // TODO: handle errors properly

  return res.body
}

export const reactivateSubscription = async (
  subscriptionId: number,
  reactivateAt?: string | null,
) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id/reactivate error: id is required')

  const res = await client(`/subscriptions/${subscriptionId}/reactivate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: {
      reactivate_at: reactivateAt,
    },
  })

  return res.body
}

export const setSubscriptionNextBillingDate = async (
  subscriptionId: number,
  billingDate: string,
) => {
  if (!subscriptionId)
    throw new Error('/subscriptions/:id/reschedule error: id is required')
  if (!billingDate)
    throw new Error('/subscriptions/:id/reschedule error: date is required')

  const res = await client<{ error?: string }>(
    `/subscriptions/${subscriptionId}/reschedule`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: {
        date: billingDate,
      },
    },
  )

  if (res.body.error) {
    throw new Error(res.body.error)
  }

  return res.body
}

export const fetchCampaign = async (
  campaignId: number,
  subscriptionId?: number,
) => {
  if (!campaignId) throw new Error('/campaigns/:id error: id is required')

  const params = new URLSearchParams({
    ...(subscriptionId && { subscription_id: subscriptionId.toString() }),
  })

  const res = await client<Campaign>(
    `/campaigns/${campaignId}${params.size ? `?${params.toString()}` : ''}`,
  )

  return res.body
}

export const fetchSubscriptionCampaigns = async (
  subscriptionId: number,
): Promise<SubscriptionCampaign[]> => {
  if (!subscriptionId)
    throw new Error(
      '/subscriptions/:subscriptionId/campaigns error: subscriptionId is required',
    )

  const res = await client<SubscriptionCampaign[]>(
    `/subscriptions/${subscriptionId}/campaigns`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const optInToCampaign = async (
  subscriptionId: number,
  campaignId: number,
  startWeek: string,
  cleanseWeek?: string,
) => {
  if (!subscriptionId) {
    throw new Error(
      '/subscriptions/:subscriptionId/campaigns error: subscriptionId is required',
    )
  }

  const res = await client(`/subscriptions/${subscriptionId}/campaigns`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: {
      campaign_id: campaignId,
      week: startWeek,
      detox_plus_week: cleanseWeek,
    },
  })

  return res.body
}

export const optOutOfCampaign = async (
  subscriptionid: number,
  campaignId: number,
) => {
  if (!subscriptionid) {
    throw new Error(
      '/subscriptions/:subscriptionId/campaigns error: subscriptionId is required',
    )
  }

  const params = new URLSearchParams({
    campaign_id: campaignId.toString(),
  })

  const res = await client(
    `/subscriptions/${subscriptionid}/campaigns?${params.toString()}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    },
  )

  return res.body
}

export const fetchShopifyOrders = async (subscriptionId: number) => {
  if (!subscriptionId) {
    throw new Error('/shopify_orders error: subscriptionId is required')
  }

  const res = await client(
    `/shopify_orders?subscription_id=${subscriptionId}`,
    { headers: getAuthHeaders() },
  )

  return res.body
}

export const redeemPointsOnSubscription = async (
  subscriptionId: number,
  redemptionOptionId: number,
) => {
  if (!subscriptionId) {
    throw new Error('/yotpo/redemptions error: subscriptionId is required')
  }

  const res = await client(`/yotpo/redemptions`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: {
      subscription_id: subscriptionId,
      redemption_option_id: redemptionOptionId,
    },
  })

  return res.body
}

export const fetchOrderTrackingDetails = async (
  trackingNumbers: string[],
  trackingCompanies: string[],
) => {
  if (!trackingNumbers) {
    throw new Error('/trackings error: trackingNumbers is required')
  }

  const params = new URLSearchParams({
    tracking_numbers: trackingNumbers.join(','),
    tracking_companies: trackingCompanies.join(','),
  })

  const res = await client(`/trackings?${params.toString()}`)

  return res.body
}

export const applyDiscountOnUpcomingWeek = async (subscriptionId: number) => {
  if (!subscriptionId) {
    throw new Error('/brightback/discount error: subscriptionId is required')
  }

  const res = await client<{ message: string }>(
    `/brightback/discount?id=${subscriptionId}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
    },
  )

  return res.body.message
}

export const activateAccount = async (email: string) => {
  const res = await client(`/customers/activate_account`, {
    method: 'POST',
    body: {
      email,
    },
  })

  // TODO: handle errors properly

  return res.body
}

export const createCustomer = async (
  email: string,
  firstName: string,
  lastName: string,
  password: string,
  passwordConfirmation: string,
) => {
  const res = await client('/customers', {
    method: 'POST',
    body: {
      email,
      first_name: firstName,
      last_name: lastName,
      password,
      password_confirmation: passwordConfirmation,
    },
  })

  return res.body
}

export const updateCustomer = async (
  customerId: number | string,
  email: string,
  firstName: string,
  lastName: string,
) => {
  const res = await client(`/customers/${customerId}`, {
    method: 'PUT',
    body: {
      email,
      first_name: firstName,
      last_name: lastName,
    },
  })

  return res.body
}

export const getCalculatedDeliveryDates = async (
  week: string,
  startDate: string,
  deliveryZoneId: string,
  deliveryWindowId: string,
  sku: string,
) => {
  const res = await client<{ delivery_dates: string[] }>(
    `/subscriptions/calculate_delivery_dates?week=${week}&start_date=${startDate}&delivery_zone_id=${deliveryZoneId}&delivery_window_id=${deliveryWindowId}&sku=${sku}`,
  )

  return res.body.delivery_dates
}

export const getCustomerPackages = async (
  customerId: number,
  {
    page,
    perPage,
    sortBy,
    filterBy,
    skipLogoutWhenNotAuthorized,
  }: {
    page?: number
    perPage?: number
    sortBy?: string
    filterBy?: string
    skipLogoutWhenNotAuthorized?: boolean
  } = {},
) => {
  if (!customerId)
    throw new Error(
      '/customers/:customerId/packages error: customerId is required',
    )

  const params = new URLSearchParams({
    ...(page && { page: page?.toString() }),
    ...(perPage && { per_page: perPage?.toString() }),
    ...(sortBy && { sort_by: sortBy }),
    ...(filterBy && { filter_by: filterBy }),
  })
  const res = await client<PopsPackage[]>(
    `/customers/${customerId}/pack_orders?${params.toString()}`,
    {
      headers: getAuthHeaders(),
      skipLogoutWhenNotAuthorized,
    },
  )

  return res.body
}

export const getPackage = async (packageId: number) => {
  if (!packageId) throw new Error('/pack_orders/:id error: id is required')

  const res = await client<PopsPackage>(`/pack_orders/${packageId}`, {
    headers: getAuthHeaders(),
  })

  return res.body
}

export const getPackageAvailableWeeks = async (
  packageId: number,
  zipCode: string,
) => {
  if (!packageId)
    throw new Error('/pack_orders/:id/available_weeks error: id is required')

  const res = await client<{ available_weeks: string[] }>(
    `/pack_orders/${packageId}/available_weeks?week_count=${PACK_OF_WEEKS_REDEMPTION_WEEK_CAP}&zip_code=${zipCode}`,
    {
      headers: getAuthHeaders(),
    },
  )

  return res.body.available_weeks
}

export const getPackageAvailableWeeksByZipCode = async ({
  zipCode,
  weekCount,
}: {
  zipCode: string
  weekCount: number
}) => {
  if (!zipCode)
    throw new Error(
      'pack_orders/available_weeks_by_zip_code error: zipCode is required',
    )

  const params = new URLSearchParams({
    zip_code: zipCode,
    week_count: weekCount.toString(),
  })

  const res = await client<{ available_weeks: string[] }>(
    `/pack_orders/available_weeks_by_zip_code?${params.toString()}`,
    {
      headers: getAuthHeaders(),
    },
  )

  return res.body.available_weeks
}

export const createPackageRedemption = async (
  packageId: number,
  week: string,
  shippingAddress?: Address,
) => {
  if (!packageId || !week)
    throw new Error(
      '/pack_orders/:packageId/redemptions error: packageId and week is required',
    )

  const res = await client(
    `/pack_orders/${packageId}/redemptions?week=${week}`,
    {
      method: 'POST',
      headers: getAuthHeaders(),
      body: {
        shipping_address: shippingAddress,
      },
    },
  )

  return res.body
}

export const removePackageRedemption = async (
  packageId: number,
  packOrderRedemptionId: number,
) => {
  if (!packageId || !packOrderRedemptionId)
    throw new Error(
      '/pack_orders/:packageId/redemptions/:packOrderRedemptionId error: packageId and week is required',
    )

  const res = await client(
    `/pack_orders/${packageId}/redemptions/${packOrderRedemptionId}`,
    {
      method: 'DELETE',
      headers: getAuthHeaders(),
    },
  )

  return res.body
}
